import { sparkToast } from '@spark';
import { data_from_server_get, data_from_server_post } from '@/shared/http/client';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import {
    getUserFacingDatasetName,
    stripCloneSuffix,
} from '@/features/datasets/utils/datasetCloneNames';

/**
 * Mirrors `optuneutils.dataset.public_dataset_names`: the Retrieval
 * Agent allocates `{task}_public` (run 1) then `{task}_public{n}` (n >= 2);
 * `_public1` is unused. Same shape as the `_ai{n}` helpers in
 * `aiGeneratedDatasetDefaults.js`.
 */
const PUBLIC_DATASET_RE = /^(.+)_public(?:([2-9]|[1-9]\d+))?$/;
const MAX_RETRIEVAL_AGENT_DATASET_NAME_LENGTH = 40;

/**
 * Parse a Retrieval Agent session file name after clone-suffix strip.
 * @returns {{ stem: string, index: number, stripped: string } | null}
 */
export function parseRetrievalAgentDatasetName(datasetName) {
    if (!datasetName || typeof datasetName !== 'string') return null;
    const stripped = stripCloneSuffix(datasetName) || datasetName;
    const match = stripped.match(PUBLIC_DATASET_RE);
    if (!match) return null;
    return {
        stem: match[1],
        index: match[2] ? Number(match[2]) : 1,
        stripped,
    };
}

export function formatRetrievalAgentDatasetName(
    usecaseName,
    index,
    maxLength = MAX_RETRIEVAL_AGENT_DATASET_NAME_LENGTH,
) {
    const suffix = index <= 1 ? '_public' : `_public${index}`;
    const stemSource = usecaseName && typeof usecaseName === 'string' ? usecaseName : 'usecase';
    const maxStem = Math.max(1, maxLength - suffix.length);
    return `${stemSource.slice(0, maxStem)}${suffix}`;
}

/**
 * Name-based Retrieval Agent detection, used before the dataset_info doc
 * (origin / source_config) has loaded so the "AI-retrieved" pill can render
 * without waiting on the analysis fetch. When *usecaseName* is provided the
 * stem must match the allocator's output for that task.
 */
export function isRetrievalAgentDatasetName(datasetName, usecaseName) {
    const parsed = parseRetrievalAgentDatasetName(datasetName);
    if (!parsed) return false;
    if (!usecaseName) return true;
    return formatRetrievalAgentDatasetName(usecaseName, parsed.index) === parsed.stripped;
}

/** Prefill Active Labels / Entity types from counts, then usecase catalogs. */
export function seedLabelNames(usecase, targets = []) {
    const fromTargets = (targets || []).map((target) => target?.name).filter(Boolean);
    if (fromTargets.length) return fromTargets;
    if (usecase?.task_type === TaskType.NER) {
        return [
            ...new Set([
                ...Object.keys(usecase?.entity_names_count || {}),
                ...(usecase?.manually_added_entities || []),
            ]),
        ];
    }
    return [
        ...new Set([
            ...Object.keys(usecase?.label_names_count || {}),
            ...Object.keys(usecase?.label_descriptions || {}),
            ...(usecase?.manually_added_labels || []),
        ]),
    ];
}

/**
 * Task types with a Retrieval Agent modal flow (Figma Data Collection).
 */
export function isRetrievalAgentSupported({ taskType }) {
    return [
        TaskType.classification,
        TaskType.NER,
        TaskType.summarization,
        TaskType.contentGeneration,
    ].includes(taskType);
}

export function retrievalTaskTypeLabel(usecase) {
    const taskType = usecase?.task_type;
    if (taskType === TaskType.NER) return 'Named Entity Recognition';
    if (taskType === TaskType.summarization) return 'Summarization';
    if (taskType === TaskType.contentGeneration) return 'Content Generation';
    if (taskType === TaskType.classification && usecase?.is_multilabel) {
        return 'Multi-label Classification';
    }
    return 'Single-label Classification';
}

const trimOrNull = (text) => {
    const trimmed = (text || '').trim();
    return trimmed || null;
};

const cleanList = (values) => {
    const next = (values || []).map((value) => (typeof value === 'string' ? value.trim() : value)).filter(Boolean);
    return next.length ? next : undefined;
};

/**
 * Wizard example rows → `PublicDatasetAgentExample` wire objects.
 *
 * Generative rows keep the wanted output in ``text`` and the optional paired
 * input in ``input_text``; NER rows derive ``labels`` from the entity spans
 * tagged in the text editor (falling back to a prefilled ``labels`` list from
 * older sessions); multilabel rows use ``labels``; single-label
 * classification keeps the legacy ``label`` field. Every example the user
 * enters is included (the wizard has no exclude option), so ``include`` is
 * always true.
 */
export function toWireExamples({ rows, taskType, isMultilabel = false }) {
    const generative = isGenerativeTask(taskType);
    return (rows || []).flatMap((row) => {
        const text = (row.text || '').trim();
        if (!text) return [];
        if (generative) {
            const inputText = row.mode === 'output' ? null : trimOrNull(row.inputText);
            return [{ text, input_text: inputText, include: true }];
        }
        if (taskType === TaskType.NER) {
            const taggedTypes = [
                ...new Set((row.entities || []).map((entity) => entity?.type).filter(Boolean)),
            ];
            return [
                {
                    text,
                    labels: taggedTypes.length ? taggedTypes : cleanList(row.labels) || null,
                    include: true,
                },
            ];
        }
        if (isMultilabel) {
            return [
                {
                    text,
                    labels: cleanList(row.labels) || null,
                    include: true,
                },
            ];
        }
        return [{ text, label: trimOrNull(row.label), include: true }];
    });
}

/**
 * Modal state → `POST retrieval-agent/start` request body.
 *
 * @param {Object} args
 * @param {Object} args.usecase Active usecase (client_id / _id).
 * @param {Object} args.payload Retrieval Agent wizard payload.
 */
export function buildRetrievalAgentRequest({ usecase, payload }) {
    const examples = toWireExamples({
        rows: payload.examples,
        taskType: usecase?.task_type,
        isMultilabel: Boolean(usecase?.is_multilabel),
    });
    const descriptions = Object.fromEntries(
        Object.entries(payload.labelDescriptions || {}).filter(([, value]) => trimOrNull(value))
    );

    return {
        client_id: usecase.client_id,
        usecase_id: usecase._id,
        dataset_name: trimOrNull(payload.datasetName) || undefined,
        goal: trimOrNull(payload.goal),
        languages: cleanList(payload.languages),
        label_names: cleanList(payload.labelNames),
        label_descriptions: Object.keys(descriptions).length ? descriptions : undefined,
        examples: examples.length ? examples : undefined,
        dataset_size_limit: Number.isFinite(payload.recordLimit) ? payload.recordLimit : undefined,
        domain: trimOrNull(payload.domain),
        agent_model: payload.agentModel?.name
            ? { name: payload.agentModel.name, provider: payload.agentModel.provider }
            : undefined,
        tone_styles: cleanList(payload.toneStyles),
        text_length: payload.textLength || undefined,
        content_type: payload.contentType || undefined,
        custom_guidelines: trimOrNull(payload.customGuidelines),
        datasets_to_exclude: cleanList(payload.datasetsToExclude),
    };
}

export function runRetrievalAgent({ usecase, payload, seedRetrievalAgentProgress }) {
    if (!usecase) {
        sparkToast.error('Task not loaded yet');
        return Promise.reject(new Error('Task not loaded yet'));
    }
    return startRetrievalAgent({ usecase, payload })
        .then((res) => {
            seedRetrievalAgentProgress?.({
                usecaseId: usecase._id,
                sessionId: res?.session_id,
                datasetName: res?.dataset_name,
                status: res?.status || 'queued',
                jobsAhead: res?.jobs_ahead,
            });
            sparkToast.success(
                `Retrieval Agent started. Dataset: ${getUserFacingDatasetName(res?.dataset_name) || 'queued'}`
            );
            return res;
        })
        .catch((err) => {
            sparkToast.error(`Failed to start Retrieval Agent: ${err?.message || err}`);
            return Promise.reject(err);
        });
}

/** Past session configs for the load-previous dropdown; empty list on failure. */
export async function fetchRetrievalAgentSessions(usecaseId) {
    if (!usecaseId) return [];
    try {
        let sessions = [];
        await data_from_server_get(`retrieval-agent/sessions/${usecaseId}`, (res) => {
            sessions = Array.isArray(res?.sessions) ? res.sessions : [];
        });
        return sessions;
    } catch {
        return [];
    }
}

/**
 * Default (next free ``{task}_public{n}``) dataset name plus taken names for
 * the Dataset Name field's inline uniqueness validation.
 */
export async function fetchRetrievalDatasetNameSuggestion(usecaseId) {
    if (!usecaseId) return { defaultName: '', existingNames: [] };
    try {
        let suggestion = { defaultName: '', existingNames: [] };
        await data_from_server_get(`retrieval-agent/dataset-name-suggestion/${usecaseId}`, (res) => {
            suggestion = {
                defaultName: res?.default_name || '',
                existingNames: Array.isArray(res?.existing_names) ? res.existing_names : [],
            };
        });
        return suggestion;
    } catch {
        return { defaultName: '', existingNames: [] };
    }
}

/** "September 6, 2026 • 12:35 PM" timestamp used in the load-previous dropdown rows. */
export function sessionOptionTimestamp(isoDatetime) {
    if (!isoDatetime) return '';
    const date = new Date(isoDatetime);
    if (Number.isNaN(date.getTime())) return '';
    const day = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${day} • ${time}`;
}

/** Left column of a load-previous dropdown row: the request preview. */
export function sessionOptionTitle(session) {
    return trimOrNull(session?.goal) || session?.dataset_name || 'Previous configuration';
}

/** Labels for the normalized run status the sessions endpoints attach to each row. */
const SESSION_STATUS_LABELS = {
    completed: 'Completed',
    failed: 'Failed',
    cancelled: 'Cancelled',
    in_progress: 'In progress',
};

/** Human label ("Failed", "Completed", ...) for a session's run status; '' when unknown. */
export function sessionStatusLabel(session) {
    return SESSION_STATUS_LABELS[session?.status] || '';
}

/** Description of a load-previous dropdown row: creator • timestamp • run status. */
export function sessionOptionDescription(session) {
    return [
        trimOrNull(session?.created_by_name),
        sessionOptionTimestamp(session?.created_datetime),
        sessionStatusLabel(session),
    ]
        .filter(Boolean)
        .join(' • ');
}

let prefillRowSeq = 0;

/**
 * Drop a stored agent model whose provider (or model) is no longer in the
 * current ``GET /available-models`` catalog.
 *
 * Past sessions keep the ``agent_model`` they ran with. Re-applying it
 * verbatim after the organization removed that provider's credentials sends a
 * provider the gateway cannot serve. Returns ``null`` when ``agentModel`` is
 * missing a name or provider, when ``availableProviders`` has no key for the
 * provider, or when the provider lists models and the stored id is not among
 * them; otherwise returns ``agentModel`` unchanged so the caller falls back to
 * the usecase default exactly like a session without a stored model.
 *
 * @param {{ name: string, provider: string } | null | undefined} agentModel
 * @param {Object} availableProviders - ``useCommercialModels().availableProviders``.
 * @returns {{ name: string, provider: string } | null}
 */
export function sanitizeAgentModel(agentModel, availableProviders) {
    if (!agentModel?.name || !agentModel?.provider) return null;
    const provider = availableProviders?.[agentModel.provider];
    if (!provider) return null;
    const models = Array.isArray(provider.models) ? provider.models : [];
    if (models.length && !models.some((model) => model?.id === agentModel.name)) return null;
    return agentModel;
}

/** Session config snapshot → wizard prefill state (camelCase example rows). */
export function sessionToWizardPrefill(session, { taskType } = {}) {
    const generative = isGenerativeTask(taskType);
    const examples = (session?.examples || []).flatMap((example) => {
        const text = (example?.text || '').trim();
        if (!text) return [];
        const labels = cleanList(example?.labels) || cleanList(example?.label ? [example.label] : []) || [];
        return [
            {
                id: `prefill-${(prefillRowSeq += 1)}`,
                text,
                label: example?.label || labels[0] || '',
                labels,
                // Sessions store entity type names, not span positions, so
                // prefilled NER examples start with no highlighted spans.
                entities: [],
                inputText: example?.input_text || '',
                mode: generative && !example?.input_text ? 'output' : 'io',
            },
        ];
    });
    return {
        goal: session?.goal || '',
        languages: cleanList(session?.languages) || [],
        labelNames: cleanList(session?.label_names) || [],
        labelDescriptions: session?.label_descriptions || {},
        examples,
        recordLimit: Number.isFinite(session?.dataset_size_limit) ? session.dataset_size_limit : null,
        domain: session?.domain || null,
        agentModel: session?.agent_model?.name
            ? { name: session.agent_model.name, provider: session.agent_model.provider }
            : null,
        toneStyles: cleanList(session?.tone_styles) || [],
        textLength: session?.text_length || null,
        contentType: session?.content_type || null,
        customGuidelines: session?.custom_guidelines || '',
        datasetsToExclude: cleanList(session?.datasets_to_exclude) || [],
    };
}

export function startRetrievalAgent({ usecase, payload }) {
    if (!usecase?._id || !usecase?.client_id) {
        return Promise.reject(new Error('Missing task identifiers'));
    }

    const taskType = usecase?.task_type;
    if (!isRetrievalAgentSupported({ taskType })) {
        return Promise.reject(new Error('Retrieval Agent is not supported for this task type.'));
    }

    return new Promise((resolve, reject) => {
        data_from_server_post(
            'retrieval-agent/start',
            buildRetrievalAgentRequest({ usecase, payload }),
            (res) => resolve(res),
            (err) => reject(new Error(err))
        );
    });
}
